import { useState, useRef, useCallback, type ReactNode } from 'react'
import { ArrowDown, Loader2 } from 'lucide-react'

interface PullToRefreshProps {
  onRefresh: () => Promise<void>
  children: ReactNode
}

const THRESHOLD = 60
const MAX_PULL = 80
const RESISTANCE = 0.4

export default function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const startYRef = useRef(0)
  const pullingRef = useRef(false)

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (isRefreshing) return
    if (window.scrollY <= 0) {
      startYRef.current = e.touches[0].clientY
      pullingRef.current = true
    }
  }, [isRefreshing])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!pullingRef.current || isRefreshing) return
    const delta = e.touches[0].clientY - startYRef.current
    if (delta > 0) {
      const distance = Math.min(delta * RESISTANCE, MAX_PULL)
      setPullDistance(distance)
    } else {
      pullingRef.current = false
      setPullDistance(0)
    }
  }, [isRefreshing])

  const handleTouchEnd = useCallback(async () => {
    if (!pullingRef.current || isRefreshing) return
    pullingRef.current = false

    if (pullDistance >= THRESHOLD) {
      setIsRefreshing(true)
      setPullDistance(THRESHOLD)
      try {
        await onRefresh()
      } finally {
        setIsRefreshing(false)
        setPullDistance(0)
      }
    } else {
      setPullDistance(0)
    }
  }, [pullDistance, isRefreshing, onRefresh])

  const isPastThreshold = pullDistance >= THRESHOLD
  const rotation = Math.min((pullDistance / THRESHOLD) * 180, 180)

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      <div
        className="flex items-center justify-center overflow-hidden"
        style={{
          height: pullDistance > 0 || isRefreshing ? `${pullDistance}px` : 0,
          transition: pullingRef.current ? 'none' : 'height 0.3s ease',
        }}
      >
        {isRefreshing ? (
          <Loader2 className="h-6 w-6 text-primary-600 animate-spin" />
        ) : (
          <ArrowDown
            className={`h-6 w-6 transition-colors ${
              isPastThreshold ? 'text-primary-600' : 'text-gray-400'
            }`}
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: pullingRef.current ? 'none' : 'transform 0.3s ease',
            }}
          />
        )}
      </div>

      {/* Page content */}
      {children}
    </div>
  )
}
